import React from 'react'
import * as R from 'remeda'

import {
  ItemMultiSelector,
  type ItemMultiSelectorProps,
} from '~/components/elements/ItemMultiSelector'
import { type SurveyGroupCustomerAttribute as SurveyGroupCustomerAttributeBase } from '~/gql/graphql'

type SurveyGroupCustomerAttribute = App.PickGraphQL<
  SurveyGroupCustomerAttributeBase,
  'name'
> & {
  idLike: string
}

export type SurveyGroupCustomerAttributeMultiSelectorProps<
  TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute,
> = Omit<
  ItemMultiSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributes: TSurveyGroupCustomerAttribute[]
  onChange?: (
    customerAttributeIdLike: TSurveyGroupCustomerAttribute['idLike'][],
  ) => void
  value: TSurveyGroupCustomerAttribute['idLike'][]
}

export const SurveyGroupCustomerAttributeMultiSelector = React.forwardRef(
  <TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute>(
    {
      customerAttributes,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupCustomerAttributeMultiSelectorProps<TSurveyGroupCustomerAttribute>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const value = React.useMemo(
      () =>
        customerAttributes.filter(({ idLike }) => valueProp.includes(idLike)),
      [customerAttributes, valueProp],
    )

    const handleChange = React.useCallback<
      EH<ItemMultiSelectorProps, 'onChange'>
    >(
      (value: TSurveyGroupCustomerAttribute[]) => {
        onChange?.(value.map(R.prop('idLike')))
      },
      [onChange],
    )

    return (
      <ItemMultiSelector
        ref={ref}
        data={customerAttributes}
        dataItemKey="idLike"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
